// src/pages/TenantSettings.tsx

import React, { useState, useEffect } from 'react';
import { Settings, Plus, RotateCcw, Download, Edit3, Users, Package, Target, UserPlus, Building2, ChevronDown, AlertCircle } from 'lucide-react';
import { TenantConfiguration, tenantConfigAPI } from '../services/tenantConfigAPI';
import { tenantManagementAPI } from '../services/tenantManagementAPI';
import { TenantRecord } from '../types/tenant';
import EditSettingModal from '../components/admin/EditSettingModal';
import CategoryManager from '../components/admin/CategoryManager';
import RoleTemplateManager from '../components/admin/RoleTemplateManager';
import { CategoryHierarchy } from '../types/category';
import { logger } from '../utils/logger';

const tenantSettingsLogger = logger.createServiceLogger('TenantSettings');

interface SettingGroup {
  title: string;
  icon: React.ReactNode;
  settings: SettingItem[];
}

interface SettingItem {
  key: string;
  name: string;
  description: string;
  icon: string;
  lastModified?: string;
  valueCount?: number;
  isSystemDefault?: boolean;
  isSpecial?: boolean; // For custom components like categories
}

const TenantSettings: React.FC = () => {
  const [configurations, setConfigurations] = useState<TenantConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSetting, setSelectedSetting] = useState<TenantConfiguration | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showRoleTemplateManager, setShowRoleTemplateManager] = useState(false);
  const [categories, setCategories] = useState<CategoryHierarchy[]>([]);
  
  // Tenant context switching for SuperAdmin
  const [availableTenants, setAvailableTenants] = useState<TenantRecord[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedTenantName, setSelectedTenantName] = useState<string>('');
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [tenantsLoading, setTenantsLoading] = useState<boolean>(false);
  const [contextLoaded, setContextLoaded] = useState<boolean>(false);

  // FIXED: Check user role and setup context - with proper retry logic
  useEffect(() => {
    const initializeContext = () => {
      const userRole = localStorage.getItem('userRole') || '';
      const tenantId = localStorage.getItem('tenantId') || '';
      const userName = localStorage.getItem('userName') || '';
      
      tenantSettingsLogger.debug('Loading context', { userRole, tenantId, userName });
      
      // FIXED: Proper retry logic with exponential backoff and maximum retries
      if (!userRole || !tenantId) {
        const retryCount = (window as any).tenantSettingsRetryCount || 0;
        const maxRetries = 5;
        
        if (retryCount < maxRetries) {
          (window as any).tenantSettingsRetryCount = retryCount + 1;
          const backoffDelay = Math.min(500 * Math.pow(1.5, retryCount), 3000); // Exponential backoff, max 3s
          
          tenantSettingsLogger.debug('Missing context, retrying', { retryCount: retryCount + 1, maxRetries, backoffDelay });
          setTimeout(initializeContext, backoffDelay);
          return;
        } else {
          // Stop retrying and show error
          tenantSettingsLogger.error('Failed to load user context after maximum retries');
          setError('Unable to load user context. Please refresh the page or try logging in again.');
          setContextLoaded(true);
          delete (window as any).tenantSettingsRetryCount;
          return;
        }
      }
      
      // Clear retry counter on success
      delete (window as any).tenantSettingsRetryCount;
      
      // FIXED: Proper role-based initialization
      if (userRole === 'SuperAdmin' && tenantId === 'SYSTEM') {
        setIsSuperAdmin(true);
        loadAvailableTenants(); // Only SuperAdmin can list tenants
      } else if (userRole === 'OrgAdmin' || (userRole === 'SuperAdmin' && tenantId !== 'SYSTEM')) {
        // OrgAdmin or SuperAdmin acting as OrgAdmin - use their tenant directly
        setIsSuperAdmin(false);
        setSelectedTenantId(tenantId);
        setSelectedTenantName(userName || tenantId || 'Current Department');
      } else {
        // Invalid role for this component
        setError(`Invalid role '${userRole}' for tenant settings. Please contact your administrator.`);
        setContextLoaded(true);
        return;
      }
      
      setContextLoaded(true);
    };
    
    // Start initialization
    initializeContext();
    
    // Cleanup function to clear any pending timeouts
    return () => {
      delete (window as any).tenantSettingsRetryCount;
    };
  }, []);

  // Load configurations when tenant selection changes
  useEffect(() => {
    if (selectedTenantId && contextLoaded) {
      loadConfigurations();
      loadCategories();
    }
  }, [selectedTenantId, contextLoaded]);

  const loadAvailableTenants = async () => {
    setTenantsLoading(true);
    try {
      const response = await tenantManagementAPI.listTenants({ status: 'all', limit: 100 });
      
      if (response.success && response.data) {
        const activeTenants = response.data.tenants.filter(tenant => 
          tenant.status === 'ACTIVE' || tenant.status === 'TRIAL' || tenant.status === 'PENDING_SETUP'
        );
        
        setAvailableTenants(activeTenants);
        
        // Auto-select first tenant if none selected
        if (activeTenants.length > 0 && !selectedTenantId) {
          const firstTenant = activeTenants[0];
          setSelectedTenantId(firstTenant.tenantId);
          setSelectedTenantName(firstTenant.organizationName);
        }
      } else {
        throw new Error(response.error?.message || 'Failed to load tenants');
      }
    } catch (err: any) {
      setError('Failed to load available tenants: ' + err.message);
      tenantSettingsLogger.error('Error loading tenants', { error: err.message });
    } finally {
      setTenantsLoading(false);
    }
  };

  const handleTenantChange = (tenantId: string) => {
    const tenant = availableTenants.find(t => t.tenantId === tenantId);
    if (tenant) {
      setSelectedTenantId(tenantId);
      setSelectedTenantName(tenant.organizationName);
      
      // Clear current configurations while loading new ones
      setConfigurations([]);
      setCategories([]);
    }
  };

const loadCategories = async () => {
  if (!selectedTenantId) return;
  
  try {
    // Load categories from tenant configuration with context switching
     const response = await tenantConfigAPI.getConfiguration('inventory.categories');
     tenantSettingsLogger.debug('Categories API response received');
    if (response.success && response.data) {
      // @ts-ignore - API returns nested data structure
      const categoryConfig = response.data.data?.configuration || response.data.configuration;
      if (categoryConfig?.values && categoryConfig.values.length > 0) {
        try {
          const categoriesData = JSON.parse(categoryConfig.values[0]);
          setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        } catch (parseError) {
          tenantSettingsLogger.error('Error parsing categories', { error: parseError });
          setCategories([]);
        }
      }
    }
  } catch (error) {
    tenantSettingsLogger.error('Error loading categories', { error });
    setCategories([]);
  }
};

  const loadConfigurations = async () => {
    if (!selectedTenantId) {
      tenantSettingsLogger.debug('No tenantId available for loading configurations');
      return;
    }
    
    tenantSettingsLogger.debug('Loading configurations for tenant', { tenantId: selectedTenantId });
    setLoading(true);
    setError(null);
    
    try {
      // Load configurations with tenant context - remove the tenantId parameter since API should use token
     const response = await tenantConfigAPI.getAllConfigurations();
      
if (response.success && response.data) {
  // @ts-ignore - API returns nested data structure
  const configs = response.data.data?.configurations || response.data.configurations || [];
  setConfigurations(configs);
        
        if (response.data.initialized) {
          tenantSettingsLogger.info('Tenant configurations initialized with defaults', { tenantName: selectedTenantName });
        }
      } else {
        throw new Error(response.error?.message || 'Failed to load configurations');
      }
    } catch (err: any) {
      setError(`Failed to load configurations for ${selectedTenantName}: ${err.message}`);
      tenantSettingsLogger.error('Error loading configurations', { error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const formatLastModified = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

const getConfigByKey = (key: string): TenantConfiguration | undefined => {
  return configurations?.find(config => config.configurationKey === key);
};

  const handleEditSetting = (configKey: string) => {
    if (configKey === 'inventory.categories') {
      setShowCategoryManager(true);
      return;
    }
    
    if (configKey === 'onboarding.role-templates') {
      setShowRoleTemplateManager(true);
      return;
    }
    
    const config = getConfigByKey(configKey);
    if (config) {
      setSelectedSetting(config);
      setIsEditModalOpen(true);
    }
  };

  const handleSaveSetting = (updatedSetting: TenantConfiguration) => {
    setConfigurations(prev => 
      prev.map(config => 
        config.configurationKey === updatedSetting.configurationKey 
          ? updatedSetting 
          : config
      )
    );
  };

  const handleCategoryChange = () => {
    // Reload configurations when categories change
    loadConfigurations();
    loadCategories();
  };

  // ✅ FIXED: Real implementation for category creation
  const saveCategoriestoBackend = async (updatedCategories: CategoryHierarchy[]) => {
    try {
      tenantSettingsLogger.info('Saving categories to backend', { count: updatedCategories.length });
      // Save categories to tenant configuration
      const response = await tenantConfigAPI.updateConfiguration(
        'inventory.categories',
        {
          displayName: 'Inventory Categories',
          description: 'Hierarchical equipment categories with parent-child relationships',
          values: [JSON.stringify(updatedCategories)]
        }
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to save categories');
      }

      // Update local state
      setCategories(updatedCategories);
      
      // Reload configurations to reflect changes
      await loadConfigurations();
      
      return { success: true };
    } catch (error: any) {
      tenantSettingsLogger.error('Error saving categories', { error });
      return { success: false, error: error.message };
    }
  };

  // ✅ FIXED: Real implementation for category creation
  const handleCategoryCreate = async (category: CategoryHierarchy) => {
    tenantSettingsLogger.debug('Creating category', { categoryName: category.name });
    
    try {
      // Generate proper categoryId
      const newCategory: CategoryHierarchy = {
        ...category,
        categoryId: `cat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        sortOrder: categories.length + 1
      };

      // Add to local categories array
      const updatedCategories = [...categories, newCategory];
      
      // Save to backend
      const result = await saveCategoriestoBackend(updatedCategories);
      
      if (result.success) {
        tenantSettingsLogger.info('Category created successfully');
      } else {
        throw new Error(result.error || 'Failed to create category');
      }
    } catch (error) {
      tenantSettingsLogger.error('Error creating category', { error });
      setError(`Failed to create category: ${(error as Error).message}`);
    }
  };

  // ✅ FIXED: Real implementation for category update
  const handleCategoryUpdate = async (category: CategoryHierarchy) => {
    tenantSettingsLogger.debug('Updating category', { categoryId: category.categoryId });
    
    try {
      // Update in local categories array
      const updatedCategories = categories.map(cat => 
        cat.categoryId === category.categoryId ? category : cat
      );
      
      // Save to backend
      const result = await saveCategoriestoBackend(updatedCategories);
      
      if (result.success) {
        tenantSettingsLogger.info('Category updated successfully');
      } else {
        throw new Error(result.error || 'Failed to update category');
      }
    } catch (error) {
      tenantSettingsLogger.error('Error updating category', { error });
      setError(`Failed to update category: ${(error as Error).message}`);
    }
  };

  // ✅ FIXED: Real implementation for category deletion
  const handleCategoryDelete = async (categoryId: string) => {
    tenantSettingsLogger.debug('Deleting category', { categoryId });
    
    try {
      // Remove from local categories array (including children)
      const updatedCategories = categories.filter(cat => 
        cat.categoryId !== categoryId && cat.parentCategoryId !== categoryId
      );
      
      // Save to backend
      const result = await saveCategoriestoBackend(updatedCategories);
      
      if (result.success) {
        tenantSettingsLogger.info('Category deleted successfully');
      } else {
        throw new Error(result.error || 'Failed to delete category');
      }
    } catch (error) {
     tenantSettingsLogger.error('Error deleting category', { error });
      setError(`Failed to delete category: ${(error as Error).message}`);
    }
  };

  // ✅ FIXED: Real implementation for bulk subcategory creation
  const handleBulkCreate = async (parentId: string, subcategories: string[]) => {
    tenantSettingsLogger.debug('Bulk creating subcategories', { parentId, count: subcategories.length });
    
    try {
      // Create new subcategory objects
      const newSubcategories: CategoryHierarchy[] = subcategories.map((name, index) => ({
        categoryId: `subcat_${Date.now()}_${index}_${Math.random().toString(36).substr(2, 9)}`,
        parentCategoryId: parentId,
        name: name.trim(),
        description: `${name.trim()} subcategory`,
        icon: '📦',
        sortOrder: categories.length + index + 1,
        isActive: true,
        bulkItemBehavior: {
          isConsumable: false // Default to tracked items
        }
      }));

      // Add to existing categories
      const updatedCategories = [...categories, ...newSubcategories];
      
      // Save to backend
      const result = await saveCategoriestoBackend(updatedCategories);
      
      if (result.success) {
        tenantSettingsLogger.info('Subcategories created successfully', { count: subcategories.length });
      } else {
        throw new Error(result.error || 'Failed to create subcategories');
      }
    } catch (error) {
      tenantSettingsLogger.error('Error bulk creating subcategories', { error });
      setError(`Failed to create subcategories: ${(error as Error).message}`);
    }
  };

  const handleResetAll = async () => {
    const tenantName = selectedTenantName || 'this tenant';
    if (window.confirm(`Are you sure you want to reset all settings for ${tenantName} to system defaults? This cannot be undone.`)) {
      setLoading(true);
      try {
        const response = await tenantConfigAPI.initializeDefaults(selectedTenantId);
        if (response.success) {
          await loadConfigurations(); // Reload all configurations
        } else {
          setError(response.error?.message || 'Failed to reset settings');
        }
      } catch (err) {
        setError('Failed to reset settings');
        tenantSettingsLogger.error('Reset error', { error: err });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleExportSettings = () => {
    const exportData = {
      tenantId: selectedTenantId,
      tenantName: selectedTenantName,
      exportDate: new Date().toISOString(),
      configurations: configurations.map(config => ({
        key: config.configurationKey,
        displayName: config.displayName,
        description: config.description,
        values: config.values,
        isSystemDefault: config.isSystemDefault,
        lastModified: config.updatedAt
      }))
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `tenant-settings-${selectedTenantId}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Define setting groups
  const settingGroups: SettingGroup[] = [
    {
      title: 'Personnel Settings',
      icon: <Users className="w-5 h-5 text-blue-600" />,
      settings: [
        {
          key: 'user.ranks',
          name: 'Ranks',
          description: 'Available ranks for department personnel',
          icon: '👮',
          lastModified: getConfigByKey('user.ranks')?.updatedAt,
          valueCount: getConfigByKey('user.ranks')?.values.length,
          isSystemDefault: getConfigByKey('user.ranks')?.isSystemDefault
        },
        {
          key: 'user.assignments',
          name: 'Assignments',
          description: 'Available assignments/divisions for personnel',
          icon: '📋',
          lastModified: getConfigByKey('user.assignments')?.updatedAt,
          valueCount: getConfigByKey('user.assignments')?.values.length,
          isSystemDefault: getConfigByKey('user.assignments')?.isSystemDefault
        },
        {
          key: 'user.departments',
          name: 'Departments',
          description: 'Department divisions and units',
          icon: '🏢',
          lastModified: getConfigByKey('user.departments')?.updatedAt,
          valueCount: getConfigByKey('user.departments')?.values.length,
          isSystemDefault: getConfigByKey('user.departments')?.isSystemDefault
        },
        {
          key: 'user.shifts',
          name: 'Shifts',
          description: 'Available work shifts',
          icon: '🕐',
          lastModified: getConfigByKey('user.shifts')?.updatedAt,
          valueCount: getConfigByKey('user.shifts')?.values.length,
          isSystemDefault: getConfigByKey('user.shifts')?.isSystemDefault
        }
      ]
    },
    {
      title: 'Equipment Settings',
      icon: <Package className="w-5 h-5 text-green-600" />,
      settings: [
        {
          key: 'inventory.categories',
          name: 'Categories',
          description: 'Hierarchical equipment categories with parent-child relationships',
          icon: '📦',
          lastModified: getConfigByKey('inventory.categories')?.updatedAt,
          valueCount: getConfigByKey('inventory.categories')?.values.length,
          isSystemDefault: getConfigByKey('inventory.categories')?.isSystemDefault,
          isSpecial: true
        },
        {
          key: 'inventory.locations',
          name: 'Storage Locations',
          description: 'Primary storage locations for equipment',
          icon: '📍',
          lastModified: getConfigByKey('inventory.locations')?.updatedAt,
          valueCount: getConfigByKey('inventory.locations')?.values.length,
          isSystemDefault: getConfigByKey('inventory.locations')?.isSystemDefault
        },
        {
          key: 'assignment.reasons',
          name: 'Assignment Reasons',
          description: 'Reasons for equipment assignment',
          icon: '🎯',
          lastModified: getConfigByKey('assignment.reasons')?.updatedAt,
          valueCount: getConfigByKey('assignment.reasons')?.values.length,
          isSystemDefault: getConfigByKey('assignment.reasons')?.isSystemDefault
        }
      ]
    },
    {
      title: 'Onboarding Settings',
      icon: <UserPlus className="w-5 h-5 text-purple-600" />,
      settings: [
        {
          key: 'onboarding.role-templates',
          name: 'Role Templates',
          description: 'Define equipment packages for different officer roles and positions',
          icon: '📋',
          isSpecial: true
        }
      ]
    }
  ];

  // Wait for context to load
  if (!contextLoaded) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading user context...</span>
      </div>
    );
  }

  // Show loading state while getting tenant list
  if (isSuperAdmin && tenantsLoading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading available departments...</span>
      </div>
    );
  }

  // Show error if tenant loading failed
  if (isSuperAdmin && error && !selectedTenantId) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading tenant settings...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
          <div className="text-sm text-red-700">{error}</div>
        </div>
      </div>
    );
  }

  // Show Category Manager
  if (showCategoryManager) {
    return (
      <div className="space-y-6">
        {/* Context Header for SuperAdmin */}
        {isSuperAdmin && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <Building2 className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-sm text-blue-700">
                Configuring categories for: <strong>{selectedTenantName}</strong>
              </span>
            </div>
          </div>
        )}
        
        {/* Back Navigation */}
        <div className="flex items-center">
          <button
            onClick={() => setShowCategoryManager(false)}
            className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Settings
          </button>
        </div>
        
        <CategoryManager 
          categories={categories}
          onCategoryCreate={handleCategoryCreate}
          onCategoryUpdate={handleCategoryUpdate}
          onCategoryDelete={handleCategoryDelete}
          onBulkCreate={handleBulkCreate}
          isLoading={loading}
        />
      </div>
    );
  }

  // Show Role Template Manager
  if (showRoleTemplateManager) {
    return (
      <div className="space-y-6">
        {/* Context Header for SuperAdmin */}
        {isSuperAdmin && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <Building2 className="w-5 h-5 text-blue-600 mr-2" />
              <span className="text-sm text-blue-700">
                Configuring role templates for: <strong>{selectedTenantName}</strong>
              </span>
            </div>
          </div>
        )}
        
        {/* Back Navigation */}
        <div className="flex items-center">
          <button
            onClick={() => setShowRoleTemplateManager(false)}
            className="flex items-center text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Settings
          </button>
        </div>
        
        <RoleTemplateManager />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Settings className="w-7 h-7 mr-3 text-blue-600" />
            {isSuperAdmin ? 'System Configuration' : 'Tenant Settings'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isSuperAdmin 
              ? `Configure dropdown options and system settings for ${selectedTenantName}`
              : `Configure dropdown options and system settings for ${selectedTenantName}`
            }
          </p>
        </div>
      </div>

      {/* SuperAdmin Tenant Context Selector */}
      {isSuperAdmin && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Building2 className="w-5 h-5 text-blue-600 mr-3" />
              <div>
                <h3 className="text-sm font-medium text-blue-900">Operating Context</h3>
                <p className="text-xs text-blue-700">Choose which department to configure</p>
              </div>
            </div>
            
            <div className="relative">
              <select
                value={selectedTenantId}
                onChange={(e) => handleTenantChange(e.target.value)}
                className="appearance-none bg-white border border-blue-300 rounded-md px-4 py-2 pr-10 text-sm font-medium text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {availableTenants.map((tenant) => (
                  <option key={tenant.tenantId} value={tenant.tenantId}>
                    {tenant.organizationName}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-600 pointer-events-none" />
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h3>
        <div className="flex space-x-3">
          <button
            onClick={() => {/* TODO: Add custom setting modal */}}
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Custom Setting
          </button>
          <button
            onClick={handleResetAll}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset All to Defaults
          </button>
          <button
            onClick={handleExportSettings}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Settings
          </button>
        </div>
      </div>

      {/* Setting Groups */}
      {settingGroups.map((group, groupIndex) => (
        <div key={groupIndex} className="bg-white rounded-lg border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              {group.icon}
              <span className="ml-2">{group.title}</span>
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200">
            {group.settings.map((setting, settingIndex) => {
              const config = getConfigByKey(setting.key);
              const hasData = config && config.values.length > 0;
              
              return (
                <div key={settingIndex} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-2xl">{setting.icon}</span>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900 flex items-center">
                          {setting.name}
                          {setting.isSystemDefault && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                              System Default
                            </span>
                          )}
                          {setting.isSpecial && (
                            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                              Enhanced
                            </span>
                          )}
                        </h3>
                        <p className="text-sm text-gray-500">{setting.description}</p>
                        {hasData && !setting.isSpecial && (
                          <p className="text-xs text-gray-400 mt-1">
                            {config.values.length} values • Last modified: {formatLastModified(config.updatedAt)}
                          </p>
                        )}
                        {setting.isSpecial && (
                          <p className="text-xs text-gray-400 mt-1">
                            Advanced management interface available
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {!setting.isSpecial && hasData ? (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
                          {config.values.length} configured
                        </span>
                      ) : !setting.isSpecial ? (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          Not configured
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          Template System
                        </span>
                      )}
                      
                      <button
                        onClick={() => handleEditSetting(setting.key)}
                        className={`inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          setting.key === 'onboarding.role-templates'
                            ? 'text-purple-700 bg-purple-100 hover:bg-purple-200 focus:ring-purple-500'
                            : setting.isSpecial
                            ? 'text-green-700 bg-green-100 hover:bg-green-200 focus:ring-green-500'
                            : 'text-blue-700 bg-blue-100 hover:bg-blue-200 focus:ring-blue-500'
                        }`}
                      >
                        <Edit3 className="w-3 h-3 mr-1" />
                        {setting.isSpecial ? 'Manage' : 'Edit'}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Edit Setting Modal */}
      {isEditModalOpen && selectedSetting && (
        <EditSettingModal
          isOpen={isEditModalOpen}
          setting={selectedSetting}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveSetting}
        />
      )}
    </div>
  );
};

export default TenantSettings;